import React, { useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiDatabase, FiRefreshCw, FiShield, FiAlertTriangle } from 'react-icons/fi';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './ResearchValidation.css';

const gb = (bytes) => bytes == null ? '—' : (Number(bytes) / (1024 ** 3)).toFixed(0) + ' GB';

const statusTone = (status) => {
  const value = String(status || '').toUpperCase();
  if (value.includes('PASS') || value.includes('ACCEPT')) return 'good';
  if (value.includes('FAIL')) return 'bad';
  if (value.includes('BLOCK') || value.includes('CANNOT')) return 'warn';
  return 'muted';
};

const ResearchValidation = () => {
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [signalFilter, setSignalFilter] = useState('ALL');
  const [signalQuery, setSignalQuery] = useState('');

  const refresh = async () => {
    try {
      setError(null);
      const response = await api.getResearchValidationStatus();
      setSnapshot(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Research validation status is unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const gates = useMemo(() => snapshot?.provider_gates?.rows || [], [snapshot]);
  const grouped = useMemo(() => {
    const groups = {};
    gates.forEach(gate => {
      const key = gate.scope || 'OTHER';
      if (!groups[key]) groups[key] = [];
      groups[key].push(gate);
    });
    return groups;
  }, [gates]);

  const signalRows = useMemo(() => snapshot?.signals?.rows || [], [snapshot]);
  const filteredSignals = useMemo(() => {
    const query = signalQuery.trim().toLowerCase();
    return signalRows.filter(row => {
      if (signalFilter !== 'ALL' && row.phase1_status !== signalFilter) return false;
      if (!query) return true;
      return [row.id,row.signal,row.research_definition,row.prior_decision]
        .some(value => String(value || '').toLowerCase().includes(query));
    });
  }, [signalRows, signalFilter, signalQuery]);

  if (loading) return <div className="workspace-page"><LoadingSpinner /></div>;

  const phaseCounts = snapshot?.signals?.phase_counts || {};
  const blocked = snapshot?.state !== 'READY_FOR_EXPERIMENTS';

  return (
    <div className="workspace-page research-validation-page">
      <div className="page-header">
        <div className="page-header-copy">
          <div className="eyebrow">Evidence gate</div>
          <h1 className="page-title">Research Validation</h1>
          <p className="page-description">
            Provider integrity, point-in-time readiness and signal research scope before any AlgoStock filter is promoted to production.
          </p>
        </div>
        <div className="page-actions">
          <button className="ws-button" onClick={refresh}><FiRefreshCw/> Refresh</button>
        </div>
      </div>

      {error && <div className="ws-banner danger"><FiAlertTriangle/><div>{error}</div></div>}

      <div className={'research-state-banner ' + (blocked ? 'blocked' : 'ready')}>
        <div className="research-state-icon">{blocked ? <FiShield/> : <FiCheckCircle/>}</div>
        <div>
          <span className="gate-kicker">CURRENT RESEARCH STATE</span>
          <h2>{blocked ? 'Data blocked — production filter remains unfrozen' : 'Accepted research dataset available'}</h2>
          <p>
            {blocked
              ? 'No historical provider release has passed the required acceptance scope. Literature and hypotheses may guide research, but they are not economic evidence.'
              : 'At least one accepted provider release is available for the predeclared walk-forward and out-of-sample protocol.'}
          </p>
        </div>
      </div>

      {snapshot?.candidate_audit && (
        <section className="candidate-audit-card">
          <div className="candidate-audit-main">
            <div>
              <span className="gate-kicker">LATEST CANDIDATE PROVIDER AUDIT</span>
              <h3>{snapshot.candidate_audit.provider} · {snapshot.candidate_audit.sample_name}</h3>
              <p>{snapshot.candidate_audit.decision_basis}</p>
            </div>
            <span className="candidate-recommendation">{snapshot.candidate_audit.recommendation}</span>
          </div>
          <div className="candidate-audit-counts">
            <div className="good"><span>PASS</span><strong>{snapshot.candidate_audit.status_counts?.PASS || 0}</strong></div>
            <div className="bad"><span>FAIL</span><strong>{snapshot.candidate_audit.status_counts?.FAIL || 0}</strong></div>
            <div className="warn"><span>CANNOT TEST</span><strong>{snapshot.candidate_audit.status_counts?.['CANNOT TEST WITH FREE SAMPLE'] || 0}</strong></div>
            <div><span>Sample</span><strong>{snapshot.candidate_audit.scope?.stock_securities || 0} stocks · {Number(snapshot.candidate_audit.scope?.stock_bars || 0).toLocaleString()} bars</strong></div>
          </div>
          <div className="candidate-audit-note">Candidate audit evidence only · not an accepted research release · no signal alpha claim.</div>
        </section>
      )}

      {snapshot?.provider_candidates?.length > 0 && (
        <section className="provider-candidate-panel">
          <div className="section-header">
            <div>
              <h2 className="section-title">Fallback Provider Track</h2>
              <div className="section-meta">Documentation precheck only · no acceptance claim</div>
            </div>
          </div>
          <div className="provider-candidate-grid">
            {snapshot.provider_candidates.map(candidate => (
              <article className="provider-candidate-card" key={candidate.provider}>
                <div className="provider-candidate-head">
                  <strong>{candidate.provider}</strong>
                  <span>{String(candidate.status || '').replaceAll('_',' ')}</span>
                </div>
                <div className="provider-candidate-recommendation">{String(candidate.recommendation || 'UNASSESSED').replaceAll('_',' ')}</div>
                <div className="provider-candidate-line">
                  <em>Why it stays in</em>
                  <span>{candidate.documented_strengths?.[0] || 'No documented strength recorded.'}</span>
                </div>
                <div className="provider-candidate-line risk">
                  <em>Main risk</em>
                  <span>{candidate.material_risks?.[0] || 'No material risk recorded.'}</span>
                </div>
                <div className="provider-candidate-next">{candidate.next_step || 'No next step recorded.'}</div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="validation-workflow-panel">
        <div className="validation-workflow-head">
          <div>
            <span className="gate-kicker">VALIDATION WORKFLOW</span>
            <h3>From candidate data to production evidence</h3>
          </div>
          {snapshot?.provider_request && (
            <span className="provider-request-pill">
              {snapshot.provider_request.provider} · {String(snapshot.provider_request.status || '').replaceAll('_',' ')}
            </span>
          )}
        </div>

        <div className="validation-workflow-strip">
          {(snapshot?.workflow || []).map((step, index) => (
            <div className={'validation-workflow-step state-' + String(step.state || 'PENDING').toLowerCase()} key={step.id}>
              <div className="workflow-node">{index + 1}</div>
              <div className="workflow-copy">
                <strong>{step.label}</strong>
                <span>{step.detail}</span>
              </div>
              <em>{step.state}</em>
            </div>
          ))}
        </div>

        <div className="validation-action-row">
          <div>
            <span>NEXT REQUIRED ACTION</span>
            <strong>{snapshot?.next_required_step || 'No next action available.'}</strong>
          </div>
          <div className="blocker-summary">
            <span className="bad">{snapshot?.blocking_gates?.failed?.length || 0} failed gates</span>
            <span className="warn">{snapshot?.blocking_gates?.cannot_test?.length || 0} unresolved gates</span>
          </div>
        </div>
      </section>

      <div className="research-stat-grid">
        <div className="research-stat-card">
          <span>Provider gates</span>
          <strong>{snapshot?.provider_gates?.accepted || 0}<small> / {snapshot?.provider_gates?.total || 0}</small></strong>
          <em>accepted for economic research</em>
        </div>
        <div className="research-stat-card">
          <span>Candidate signals</span>
          <strong>{snapshot?.signals?.total || 0}</strong>
          <em>mapped before empirical promotion</em>
        </div>
        <div className="research-stat-card">
          <span>Phase-1 testable</span>
          <strong>{phaseCounts.PHASE1 || 0}</strong>
          <em>{phaseCounts.PARTIAL || 0} partial · {phaseCounts.DEFERRED || 0} deferred</em>
        </div>
        <div className="research-stat-card">
          <span>Historical floor</span>
          <strong className="period-value">2005–2025</strong>
          <em>1998–2025 preferred</em>
        </div>
      </div>

      <div className="research-validation-grid">
        <section className="ws-panel pad">
          <div className="section-header">
            <div>
              <h2 className="section-title">Provider Acceptance</h2>
              <div className="section-meta">PA01–PA22 · no documentation-only passes</div>
            </div>
            <span className={'validation-chip ' + (snapshot?.provider_accepted ? 'good' : 'warn')}>
              <FiDatabase/> {snapshot?.provider_accepted ? 'PROVIDER ACCEPTED' : 'NO ACCEPTED DATASET'}
            </span>
          </div>

          <div className="gate-group-stack">
            {Object.entries(grouped).map(([scope, rows]) => (
              <div className="gate-group" key={scope}>
                <div className="gate-group-title"><span>{scope}</span><em>{rows.length} gates</em></div>
                {rows.map(row => (
                  <div className="gate-row" key={row.id}>
                    <span className="gate-id">{row.id}</span>
                    <div className="gate-copy"><strong>{row.title}</strong><small>{row.scope}</small></div>
                    <span className={'gate-status-pill ' + statusTone(row.status)}>{String(row.status).replaceAll('_',' ')}</span>
                    {row.candidate_evidence && (
                      <details className="gate-evidence">
                        <summary>Audit evidence</summary>
                        <p>{row.candidate_evidence}</p>
                        {row.remaining_evidence && <div><strong>Still required</strong><span>{row.remaining_evidence}</span></div>}
                      </details>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        <div className="research-side-stack">
          <section className="ws-panel pad">
            <div className="section-header">
              <h2 className="section-title">Signal Readiness</h2>
              <span className="section-meta">78 mapped candidates</span>
            </div>
            <div className="signal-readiness-list">
              <div><span>Phase 1</span><strong>{phaseCounts.PHASE1 || 0}</strong></div>
              <div><span>Partial</span><strong>{phaseCounts.PARTIAL || 0}</strong></div>
              <div><span>Deferred</span><strong>{phaseCounts.DEFERRED || 0}</strong></div>
              <div><span>Contract only</span><strong>{phaseCounts.CONTRACT_ONLY || 0}</strong></div>
              <div><span>Out of scope</span><strong>{phaseCounts.OUT_OF_SCOPE || 0}</strong></div>
            </div>
          </section>

          <section className="ws-panel pad">
            <div className="section-header">
              <h2 className="section-title">Validation Engine</h2>
              <span className="section-meta">software readiness</span>
            </div>
            <div className="engine-readiness-list">
              <div><span>Price features</span><strong className={snapshot?.engine?.price_feature_engine ? 'good' : 'warn'}>{snapshot?.engine?.price_feature_engine ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Cross-sectional ranks</span><strong className={snapshot?.engine?.cross_sectional_percentiles ? 'good' : 'warn'}>{snapshot?.engine?.cross_sectional_percentiles ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Forward labels</span><strong className={snapshot?.engine?.forward_label_engine ? 'good' : 'warn'}>{snapshot?.engine?.forward_label_engine ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Rank / decile evaluator</span><strong className={snapshot?.engine?.rank_evaluator ? 'good' : 'warn'}>{snapshot?.engine?.rank_evaluator ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Paired ablation</span><strong className={snapshot?.engine?.paired_ablation_evaluator ? 'good' : 'warn'}>{snapshot?.engine?.paired_ablation_evaluator ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Cash / zero delistings</span><strong className={snapshot?.engine?.terminal_cash_zero_ledger ? 'good' : 'warn'}>{snapshot?.engine?.terminal_cash_zero_ledger ? 'READY' : 'PENDING'}</strong></div>
              <div><span>Stock / mixed successor chains</span><strong className={snapshot?.engine?.terminal_stock_mixed_ledger ? 'good' : 'warn'}>{snapshot?.engine?.terminal_stock_mixed_ledger ? 'READY' : 'PENDING'}</strong></div>
            </div>
          </section>

          <section className="ws-panel pad">
            <div className="section-header">
              <h2 className="section-title">Research Store</h2>
              <span className="section-meta">immutable releases</span>
            </div>
            <div className="research-store-card">
              <div><span>Store present</span><strong>{snapshot?.store?.store_present ? 'YES' : 'NO'}</strong></div>
              <div><span>Recorded releases</span><strong>{snapshot?.store?.release_count || 0}</strong></div>
              <div><span>Accepted releases</span><strong>{snapshot?.store?.accepted_release_count || 0}</strong></div>
              <div><span>Frozen experiments</span><strong>{snapshot?.store?.experiment_count || 0}</strong></div>
              <div><span>Measured experiments</span><strong>{snapshot?.store?.measured_experiment_count || 0}</strong></div>
              <div><span>Exposed holdouts</span><strong className={snapshot?.store?.exposed_holdout_count ? 'warn' : ''}>{snapshot?.store?.exposed_holdout_count || 0}</strong></div>
              <div><span>Contract</span><strong>{snapshot?.contract_version || '—'}</strong></div>
            </div>
          </section>

          <section className="ws-panel pad">
            <div className="section-header">
              <h2 className="section-title">Local Research Capacity</h2>
              <span className="section-meta">{snapshot?.system?.ready_for_full_phase1 ? 'ready' : 'capacity check'}</span>
            </div>
            <div className="research-store-card">
              <div><span>Disk free</span><strong className={snapshot?.system?.storage_ready ? 'good' : 'warn'}>{gb(snapshot?.system?.disk_free_bytes)}</strong></div>
              <div><span>Disk target</span><strong>{gb(snapshot?.system?.storage_target_bytes)}</strong></div>
              <div><span>RAM</span><strong>{gb(snapshot?.system?.memory_bytes)}</strong></div>
              <div><span>Memory mode</span><strong>{String(snapshot?.system?.memory_state || 'UNKNOWN').replaceAll('_',' ')}</strong></div>
              <div><span>Logical CPU</span><strong>{snapshot?.system?.cpu_logical || '—'}</strong></div>
              <div><span>Pipeline</span><strong>{snapshot?.system?.out_of_core_required ? 'OUT-OF-CORE' : 'IN-MEMORY OK'}</strong></div>
            </div>
            {!snapshot?.system?.storage_ready && (
              <div className="research-warning-line capacity-warning">
                <FiAlertTriangle/>
                <span>{gb(snapshot?.system?.storage_shortfall_bytes)} more free storage is required to meet the Phase-1 working target.</span>
              </div>
            )}
          </section>

          <section className="ws-panel pad">
            <div className="section-header">
              <h2 className="section-title">Next Required Step</h2>
            </div>
            <p className="research-next-step">{snapshot?.next_required_step || 'No next step available.'}</p>
            <div className="research-warning-line">
              <FiAlertTriangle/>
              <span>No stock-selection backtest may be treated as evidence until a real provider release passes the required gates.</span>
            </div>
          </section>
        </div>
      </div>

      <section className="ws-panel signal-map-panel">
        <div className="signal-map-head">
          <div>
            <span>SIGNAL RESEARCH MAP</span>
            <em>{filteredSignals.length} of {signalRows.length} hypotheses · research posture is not production proof</em>
          </div>
          <div className="signal-map-controls">
            <input
              value={signalQuery}
              onChange={event => setSignalQuery(event.target.value)}
              placeholder="Search signal or definition"
              aria-label="Search research signals"
            />
            <select value={signalFilter} onChange={event => setSignalFilter(event.target.value)} aria-label="Filter research signals">
              <option value="ALL">All phases</option>
              <option value="PHASE1">Phase 1</option>
              <option value="PARTIAL">Partial</option>
              <option value="DEFERRED">Deferred</option>
              <option value="CONTRACT_ONLY">Contract only</option>
              <option value="OUT_OF_SCOPE">Out of scope</option>
            </select>
          </div>
        </div>
        <div className="signal-map-table-wrap">
          <table className="signal-map-table">
            <thead><tr><th>ID</th><th>Signal / research definition</th><th>Data phase</th><th>Prior posture</th><th>Performance</th></tr></thead>
            <tbody>
              {filteredSignals.map(row => (
                <tr key={row.id}>
                  <td className="signal-id">{row.id}</td>
                  <td className="signal-copy">
                    <strong>{row.signal}</strong>
                    <span>{row.research_definition || '—'}</span>
                    {row.limitation_or_later_data && <small>Constraint: {row.limitation_or_later_data}</small>}
                  </td>
                  <td><span className={'signal-phase phase-' + String(row.phase1_status || '').toLowerCase()}>{String(row.phase1_status || '—').replaceAll('_',' ')}</span></td>
                  <td><span className="signal-posture">{row.prior_decision || '—'}</span></td>
                  <td><span className="signal-performance">{row.performance_status || 'NOT RUN'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredSignals.length && <div className="signal-map-empty">No signal matches this filter.</div>}
        </div>
      </section>
    </div>
  );
};

export default ResearchValidation;
